import httpStatus from 'http-status-codes';
import ErrorsFactory from './errorsFactory';

const formatErrorMessageWithDetails = ({ details }) =>
  details.map(({ message }) => message).join(',\n');

export const parseAsJson = response => {
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  throw ErrorsFactory.FetchingError('Cannot parse response as JSON', response.status);
};

/*
  Parsing chunks is following a specific structure returned from the backend
  { columns: [], types: [], rows: [] }
  Using a different structure will requiere code changes and having complex nested objects will break the implementation.
  The funcion split the rows data in small chunks to parse individually and significantly reduce the memory usage.
*/
export const parseChunksAsJson = async response => {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let isInRows = false;
  let isHeaderParsed = false;
  const processedRows = [];
  let headers = {};

  if (!reader) {
    throw new Error('Failed to get reader');
  }

  while (true) { // eslint-disable-line
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    while (buffer.includes('{') || buffer.includes('[')) {
      if (!isHeaderParsed) {
        const headerEndIndex = buffer.indexOf('"rows":[');
        if (headerEndIndex !== -1) {
          try {
            const headerString = buffer.slice(0, headerEndIndex + 7);
            headers = JSON.parse(`${headerString}[]}`);
            buffer = buffer.slice(headerEndIndex + 8);
            isHeaderParsed = true;
            isInRows = true;
          } catch (e) {
            throw new Error('Parsing headers error');
          }
        }
      }

      if (isInRows) {
        let rowEndIndex = buffer.indexOf('},');

        if (rowEndIndex === -1) {
          if (buffer.trim() === ']}' || buffer.trim() === '}') {
            break;
          }
          const lastRowEnd = buffer.indexOf('}]');
          if (lastRowEnd !== -1) {
            rowEndIndex = lastRowEnd;
          } else {
            break;
          }
        }

        if (rowEndIndex !== -1) {
          try {
            const rowString = buffer.slice(0, rowEndIndex + 1);
            processedRows.push(JSON.parse(rowString));

            if (buffer[rowEndIndex + 1] === ',') {
              buffer = buffer.slice(rowEndIndex + 2);
            } else {
              buffer = buffer.slice(rowEndIndex + 1);
            }
          } catch (e) {
            throw new Error('Parsing row error');
          }
        } else {
          break;
        }
      }
    }
  }
  return { ...headers, rows: processedRows };
};

export const handleTimeout = error => {
  if (error instanceof TypeError) {
    throw ErrorsFactory.TimeoutError();
  }

  throw error;
};

export const handleErrors = ({
  errorMessage,
  parseResponseErrorMessage = false,
  parseResponseErrorWithDetails = false,
}) => response => {
  if (!response) {
    throw ErrorsFactory.FetchingError(errorMessage);
  }
  if (response.ok && (response.status >= httpStatus.OK && response.status < httpStatus.MULTIPLE_CHOICES)) {
    return response;
  }

  if (response.status === httpStatus.UNAUTHORIZED) {
    throw ErrorsFactory.UnauthorizedError(errorMessage);
  }

  if (parseResponseErrorMessage || parseResponseErrorWithDetails) {
    return parseAsJson(response)
      .then(responseError => {
        const hasAdditionalInfo = true;
        const message = (parseResponseErrorWithDetails && responseError.details?.length > 0
          ? formatErrorMessageWithDetails(responseError)
          : responseError?.message
        ) || errorMessage;

        throw ErrorsFactory.FetchingError(
          message,
          response.status,
          hasAdditionalInfo
        );
      });
  }

  throw ErrorsFactory.FetchingError(errorMessage, response.status);
};

export const replaceParams = (url, params = {}) => Object.keys(params)
  .reduce((replacedUrl, paramKey) =>
    replacedUrl.replace(new RegExp(`:${paramKey}`), params[paramKey]), url);

export const encodeToBase64 = data => ({ data: btoa(JSON.stringify(data)) });
